import { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from '@tanstack/react-router'
import { TryOnButton } from '@/components/shared/atoms/TryOnButton'
import { TryOnInfoModal } from '@/components/try-on/TryOnInfoModal'

export const DesktopSearchBar = () => {
    const [searchActive, setSearchActive] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const [searchValue, setSearchValue] = useState('')
    const [isTryOnModalOpen, setIsTryOnModalOpen] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const navigate = useNavigate()

    // Focus input and show popup
    const handleSearchFocus = () => {
        setIsFocused(true)
        setSearchActive(true)
    }

    // Hide popup
    const handleSearchBlur = () => {
        setIsFocused(false)
        setTimeout(() => setSearchActive(false), 150) // Delay to allow click in popup
    }

    // Handle Enter key to navigate to search page
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && searchValue.trim()) {
            setSearchActive(false)
            navigate({ to: '/search', search: { q: searchValue.trim() } })
        }
    }

    // Handle input changes and reshow dropdown if needed
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value)
        // Reshow dropdown if user is focused and starts typing (after it was hidden)
        if (isFocused && !searchActive) {
            setSearchActive(true)
        }
    }

    return (
        <div className="relative flex items-center justify-center gap-2">
            <motion.div
                className="flex h-[36px] flex-1 items-center rounded-lg bg-[var(--color-bg)] px-4 py-2"
                initial={false}
                animate={{
                    boxShadow: searchActive
                        ? '0 2px 4px 0 rgba(0,0,0,0.10)'
                        : 'none',
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                style={{ cursor: 'text' }}
                onClick={() => inputRef.current?.focus()}
            >
                {/* <Search className="mr-2 h-4 w-4 text-neutral-400" /> */}
                <input
                    ref={inputRef}
                    type="text"
                    className="w-full bg-transparent text-sm leading-none text-neutral-400 outline-none placeholder:text-sm placeholder:text-neutral-400"
                    placeholder="Search"
                    value={searchValue}
                    onChange={handleInputChange}
                    onFocus={handleSearchFocus}
                    onBlur={handleSearchBlur}
                    onKeyDown={handleKeyDown}
                />
            </motion.div>
            <TryOnButton
                onClick={() => setIsTryOnModalOpen(true)}
                className="rounded-lg px-2 py-2 text-sm"
            />
            {/* Search Popup */}
            <AnimatePresence>
                {searchActive && (
                    <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.98 }}
                        transition={{ duration: 0.18 }}
                        className="absolute top-full left-1/2 z-50 mt-2 w-full -translate-x-1/2 rounded-2xl border border-gray-50 bg-white shadow-xl"
                        onMouseDown={(e) => e.preventDefault()} // Prevent blur on click
                    >
                        <div className="p-6">
                            <div>
                                {searchValue.trim() ? (
                                    <div className="text-sm text-gray-600">
                                        Press enter to search for:{' '}
                                        <span className="font-medium">
                                            {searchValue}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="text-sm text-gray-400">
                                        Start typing to search...
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Try On Info Modal */}
            <TryOnInfoModal
                open={isTryOnModalOpen}
                onOpenChange={setIsTryOnModalOpen}
            />
        </div>
    )
}
